import $ from 'jquery';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import autobind from 'class-autobind';

import map from 'lodash/map';
import min from 'lodash/min';
import each from 'lodash/each';
import defer from 'lodash/defer';
import filter from 'lodash/filter';
import debounce from 'lodash/debounce';

import { getIsHiddenOnMobile } from 'zoomforth_codesbane_viewer/lib/sections.js';

import { calculateContainerWidth } from './shared/section_manager_for_viewer.jsx';

import hasEqualProps from 'zoomforth_zfutils/has_equal_props.js';
import { isElInViewport } from 'zoomforth_zfutils/dom_utils.js';

import { SECTION_FONTSIZE_CHANGE_EVENT } from 'zoomforth_codesbane_viewer/viewer/managers/subpage_resize_event_manager.jsx';

import SectionsWrapperUi from './sections_wrapper.ui.jsx';

// TEXT_SCALE_FACTOR indicates that the text should be modified by a percent equal to one tenth of the section size.
// so at 1000px the text size is unmodified at 100%
// this should not be adjusted, in order to maintain the design of existing pages.
const TEXT_SCALE_FACTOR = 0.1;

const SECTIONS_WRAPPER_EVENT_NAMESPACE = 'sections_wrapper';

const SECTIONS_SCROLL_TIMEOUT_MS = 250;


class SectionsWrapper extends Component {

  constructor(props) {
    super(props);

    this.state = {
      sectionFontSize: props.defaultSectionFontSize || '100%'
    };

    this.sectionRefs = [];

    autobind(this);
  }

  componentDidMount() {
    $(this.props.baseScrollContainer).on(
      `scroll.${SECTIONS_WRAPPER_EVENT_NAMESPACE}`,
      debounce(this.handleSectionViewportVisibility, SECTIONS_SCROLL_TIMEOUT_MS)
    );

    // we defer this call so we can be sure that sections have been rendered in
    // place. Otherwise they can have the wrong scroll offset since they have
    // not been rendered in the document flow.
    defer(this.handleSectionViewportVisibility);

    $(window).on(`${SECTION_FONTSIZE_CHANGE_EVENT}.${SECTIONS_WRAPPER_EVENT_NAMESPACE}`, () => {
      this.setFontSizeForSections();
    });

    this.setFontSizeForSections();
  }

  handleSectionViewportVisibility() {
    if (!this.sectionRefs) {
      return;    
    }

    each(this.sectionRefs, (sectionRef)=> {
      const $sectionRef = $(sectionRef);

      if ($sectionRef.length === 0) {
        return;
      }

      if (isElInViewport(sectionRef, { partially: true })) {
        $sectionRef.addClass('is-in-viewport');
        $sectionRef.addClass('was-in-viewport');
      } else {
        $sectionRef.removeClass('is-in-viewport');
      }
    });
  }

  componentWillUnmount() {
    $(window).off(`${SECTION_FONTSIZE_CHANGE_EVENT}.${SECTIONS_WRAPPER_EVENT_NAMESPACE}`);
    $(this.props.baseScrollContainer).off(`scroll.${SECTIONS_WRAPPER_EVENT_NAMESPACE}`);
  }

  componentDidUpdate(prevProps) {
    // we should never need to adjust the font size from the default
    if (this.props.isTextResizePrevented) {
      return;
    }

    if (!hasEqualProps(prevProps, this.props, ['sections'])) {
      this.setFontSizeForSections();
    }
  }

  setFontSizeForSections() {
    const { sections, isFullPage, isUsingV1Styles, isTextResizePrevented=false } = this.props;

    // we should never need to adjust the font size from the default
    if (isTextResizePrevented) {
      return;
    }

    // empty subpages and fullpage subpages don't need font size set
    if (sections.length === 0 || isFullPage) {
      return;
    }

    // filter out tab sections since their font sizing is very different due to
    // the multiple wrapped layers. this merely excludes them from calculating
    // the minimum section width so we can get the correct baseline font size.
    const validSectionWrappers = filter($('.outer-tiles-wrapper'), (outerTilesWrapper) => {
      return $(outerTilesWrapper).parents('.section-type-tabs').length === 0;
    });

    const minSectionWidth = min(map(validSectionWrappers, (outerTilesWrapper) => {
      return calculateContainerWidth($(outerTilesWrapper), isUsingV1Styles);
    }));

    if (!minSectionWidth || minSectionWidth === 0) {
      return;
    }

    this.setState({
      sectionFontSize: `${minSectionWidth * TEXT_SCALE_FACTOR}%`
    });
  }

  handleSectionRefReceived(ref) {
    this.sectionRefs.push(ref);
  }

  render() {
    const { sectionFontSize } = this.state;
    const { sections, sectionRenderer, isMobile } = this.props;

    const filteredSections = (
      isMobile ?
        filter(sections, section=> !getIsHiddenOnMobile(section))
      :
        sections
    );

    return (
      <SectionsWrapperUi>
        {
          map(filteredSections, (section) => (
            sectionRenderer({
              section,
              sectionFontSize,
              onSectionRefReceive: this.handleSectionRefReceived
            })
          ))
        }
      </SectionsWrapperUi>
    );
  }
}

SectionsWrapper.defaultProps = {
  baseScrollContainer: window
};

SectionsWrapper.propTypes = {
  baseScrollContainer: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  sections: PropTypes.array.isRequired,
  isFullPage: PropTypes.bool.isRequired,
  isUsingV1Styles: PropTypes.bool.isRequired,
  isTextResizePrevented: PropTypes.bool.isRequired,
  sectionRenderer: PropTypes.func.isRequired
};

export default SectionsWrapper;
